import getConfig from '../../../utils/config';
import { authenticatedFetch, getFromGalaxyCDN } from '../../api';
import { ResponseData, ContentTypes, client, Query, RequestBody } from '../../galaxyClient';
import { extractPaginatedUrl } from '../../helpers/pagination';
import { ListPostsResponse, POST_STATUS, Post } from './types';

export type CreatePostBody = RequestBody<ContentTypes, '/v2/posts/', 'post'>;
export const createPost = async (body: CreatePostBody) => {
  const { data } = await client().content.POST('/v2/posts/', {
    body: {
      ...body,
      draft: false,
    },
  });

  return data;
};

export type UpdatePostBody = RequestBody<ContentTypes, '/v2/posts/{postId}', 'put'>;
export const updatePost = async (postId: string, body: UpdatePostBody): Promise<void> => {
  await client().content.PUT('/v2/posts/{postId}', {
    params: {
      path: { postId },
    },
    body,
  });
};

export const deletePost = async (postId: string) => {
  await client().content.DELETE('/v2/posts/{postId}', {
    params: {
      path: { postId },
    },
    // @ts-ignore due to incomplete type of the delete post endpoint we need to pass an empty body in here
    body: {},
  });
};

export async function getPostsCDN({
  categoryId,
  initiativeId,
  statuses,
  limit = 6,
  tag = ['Highlight'],
}: {
  initiativeId?: string[];
  categoryId?: string[];
  statuses?: Array<POST_STATUS>;
  limit?: number;
  tag?: string[] | null;
}) {
  const query = {
    categoryId,
    initiativeId,
    limit,
    status: statuses,
    tag: tag === null ? undefined : tag,
  };

  const { result: posts, pagination } = await getFromGalaxyCDN<ListPostsResponse>(
    'content/v2/posts',
    query,
  );
  return {
    posts: posts ?? [],
    url: posts?.length === limit ? pagination?.next : undefined,
  };
}

export type GetPostsResponse = ResponseData<ContentTypes, '/v2/posts/', 'get'>;
type PostsFilters = Query<ContentTypes, '/v2/posts/', 'get'>;
export const getPostsChunkedCDN = async ({
  categoryId,
  initiativeId,
  status,
  limit = 6,
  tag = ['Highlight'],
}: PostsFilters): Promise<{ posts: GetPostsResponse['result'] }> => {
  const { pages } = await client().content.GET_CHUNKED('/v2/posts/', {
    chunkKey: 'initiativeId',
    params: {
      query: {
        categoryId,
        initiativeId,
        limit,
        status,
        tag: tag === null ? undefined : tag,
      },
    },
  });

  return { posts: pages.flatMap(({ result }) => result) };
};

type PostsFilter = Query<ContentTypes, '/v2/posts/', 'get'>;
export const getPostsPaginated = async (filters?: PostsFilter, paginationUrl?: string) => {
  if (paginationUrl) {
    const config = await getConfig();
    const { data } = await client().fetch<ContentTypes, '/v2/posts/', 'get'>(
      extractPaginatedUrl(paginationUrl, config.API_URL),
    );
    return data;
  }

  const { data } = await client().content.GET('/v2/posts/', {
    params: {
      query: filters,
    },
  });
  return data;
};

export const getCategoryPosts = async ({ categoryId }: { categoryId: string }) => {
  const { data } = await client().content.GET('/v2/posts/', {
    params: {
      query: {
        categoryId: [categoryId],
      },
    },
  });
  return data;
};

export const setPosterForVideo = async (
  body: { key: string; positionInMs: number },
  videoId: string,
) =>
  await authenticatedFetch(`content/v2/videos/${videoId}/poster`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

export async function getPost(id: string) {
  return await getFromGalaxyCDN<Post>(`content/v2/posts/${id}`);
}
